import React from "react";
import classNames from "classnames";
import { Link } from "gatsby";
import ensembleLogo from "../../images/footer/ensemble-logo-white.svg";
import linkedinLogo from "../../images/footer/linkedin-logo-white.svg";
import facebookLogo from "../../images/footer/facebook-logo-white.svg";
import instagramLogo from "../../images/footer/instagram-logo-white.svg";
import footerBackgroundPng from "../../images/footer/footer-background.png";
import footerBackgroundWebp from "../../images/footer/footer-background.webp";
import globalStrings from "../../config/index";
import Image from "../Image";
import { useRegion } from "../../region/RegionProvider";

const dividerClass =
  "md:pb-0 pb-4 md:border-b-0 border-b border-white border-opacity-20";
const textClass = "text-white text-xs md:text-sm";

const Contact = () => {
  const { contact } = globalStrings.footer;
  const { region } = useRegion();
  const phone = region === "global" ? contact.main.numberGlobal : contact.main.numberEmea;

  return (
    <div className={`md:order-last space-y-4 ${dividerClass}`}>
      <div className={`font-semibold md:text-[14px] lg:text-[16px] ${textClass}`}>
        {contact.header}
      </div>

      <ul className="space-y-1">
        <li
          data-testid="contact-element"
          className={classNames(textClass, "flex flex-row")}
        >
          <div className="md:text-[12px] lg:text-[14px] lg:font-semibold whitespace-nowrap">
            {contact.main.label}&nbsp;
          </div>
          <div className="font-normal md:text-[12px] lg:text-[14px] whitespace-nowrap">
            <a href={`tel:${phone}`}>{phone}</a>
          </div>
        </li>

        {region === "emea" && (
          <li
            data-testid="contact-element"
            className={classNames(textClass, "flex flex-row")}
          >
            <div className="md:text-[12px] lg:text-[14px] lg:font-semibold">
              {contact.email.label}&nbsp;
            </div>
            <div className="font-normal md:text-[12px] lg:text-[14px]">
              <a href={`mailto:${contact.email.address}`}>
                {contact.email.address}
              </a>
            </div>
          </li>
        )}

        {region === "global" && (
          <li
            data-testid="contact-element"
            className={classNames(textClass, "flex flex-row")}
          >
            <div className="md:text-[12px] lg:text-[14px] lg:font-semibold whitespace-nowrap">
              {contact.fax.label}&nbsp;
            </div>
            <div className="font-normal md:text-[12px] lg:text-[14px] whitespace-nowrap">
              {contact.fax.number}
            </div>
          </li>
        )}
      </ul>
    </div>
  );
};

const Location = () => {
  const { region } = useRegion();
  const { locations } = globalStrings.footer;
  const locationData = region === "global" ? locations.locationsGlobal : locations.locationsEmea;

  return (
    <div className={`space-y-4 md:mt-0 mt-4 ${dividerClass}`}>
      <div className={`font-semibold md:text-[14px] lg:text-[16px] ${textClass}`}>
        {locations.header}
      </div>
      <ul className="space-y-4">
        {locationData.map((location: string) => (
          <li
            data-testid="location-element"
            key={location}
            className={`md:text-[12px] lg:text-[14px] ${textClass}`}
          >
            {location}
          </li>
        ))}
      </ul>
    </div>
  );
};

const Join = () => {
  const { join } = globalStrings.footer;
  const { region } = useRegion();

  const prefix = region === "global" ? "" : "/emea";

  return (
    <div data-testid="join-container" className={`space-y-4 md:mt-0 md:ml-1 mt-4 ${dividerClass}`}>
      <div className={`font-semibold ${textClass} mt-0 md:text-[14px] lg:text-[16px]`}>{join.header}</div>
      <div className={`${textClass} md:text-[14px] lg:text-[16px]`}>{join.description}</div>
      <Link className={`!mt-1 block underline ${textClass}`} to={`${prefix}/careers`}>
        {join.cta}
      </Link>
    </div>
  );
};

const Links = () => {
  const { privacy, cookies, terms } = globalStrings.footer;

  return (
    <div className="md:flex justify-end">
      <ul className="space-y-1 md:mt-0 mt-4 md:text-right">
        <li data-testid="link-element">
          <Link className={`${textClass}`} to="/privacy">
            {privacy}
          </Link>
        </li>
          <li data-testid="link-element">
              <Link className={textClass} to="/cookies">
                  {cookies}
              </Link>
          </li>
        <li data-testid="link-element">
          <Link className={textClass} to="/terms">
            {terms}
          </Link>
        </li>
      </ul>
    </div>
  );
};

const SocialMedia = () => {
  const { linkedin, facebook, instagram } = globalStrings.footer;

  return (
    <ul className="flex justify-center md:justify-start md:ml-10 gap-4">
      <li data-testid="social-media-element" className="inline-block">
        <a href={linkedin.url} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
          <img className="block mt-1 w-[30px] h-[30px]" src={linkedinLogo} alt={linkedin.alt} />
        </a>
      </li>
      <li data-testid="social-media-element" className="inline-block">
        <a href={instagram.url} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
          <img className="block mt-1 w-[30px] h-[30px]" src={instagramLogo} alt={instagram.alt} />
        </a>
      </li>
      <li data-testid="social-media-element" className="inline-block">
        <a href={facebook.url} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
          <img className="block mt-1 w-[30px] h-[30px]" src={facebookLogo} alt={facebook.alt} />
        </a>
      </li>
    </ul>
  );
};

const Logo = () => {
  const { logoAlt } = globalStrings.footer;

  return (
    <div data-testid="logo-container" className="p-0 md:w-full md:mx-0 mx-auto md:mb-0">
      <img src={ensembleLogo} alt={logoAlt} className="w-[240px] h-[36px] object-contain" />
    </div>
  );
};

const Copyrights = () => {
  const { region } = useRegion();
  const { copyright } = globalStrings.footer;
  const one = region === "global" ? copyright.oneGlobal : copyright.oneEmea;

  return (
    <div data-testid="copyright-container">
      <div className="text-white md:text-right text-center text-[12px] ">
        {one.replace("{year}", `${new Date().getFullYear()}`)}
      </div>
      <div className="text-white md:text-right text-center text-[12px]">
        {copyright.two}
      </div>
    </div>
  );
};

const Footer = () => {
  return (
    <footer className="relative w-full h-full z-0" aria-label="Site footer">
      <Image
        src={footerBackgroundPng}
        webpSrc={footerBackgroundWebp}
        alt="footer"
        className="absolute w-full h-full object-cover object-left md:object-bottom"
      />
      <div className="flex justify-center">
        <div className="md:flex md:flex-col md:gap-y-7 md:px-20 md:py-20 py-6 max-w-max z-10">
          <div className="md:grid grid-cols-2">
            <div className="md:grid md:grid-cols-[35%_65%] grid-cols-2">
              <Contact />
              <Location />
            </div>
            <div className="md:grid grid-cols-2 justify-between">
              <Join />
              <Links />
            </div>
          </div>
          <div className="flex flex-col md:grid md:grid-cols-[18%_auto_auto_auto] mt-6 md:mt-14  content-center ">
              <div className="mt-6 w-full flex items-center md:self-end md:mt-auto md:h-full md:pt-2">
                <Logo />
              </div>
            <div className="flex justify-center order-first md:order-none md:flex md:justify-self-start md:self-end gap-2">
              <SocialMedia />
            </div>
            <div className="hidden md:block" />
            <div className="mt-3 md:mt-auto">
              <Copyrights />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
